// What an RF receiver last heard — WHICH sensor, and what it said.
//
// The receiver (DeviceID 47) reports `RfV(<code>,<binary>)` as its status, and
// the sensor card printed exactly that: a red "alert" disc over a clipped
// string of bits. The code already identifies the sensor (rfIdentify), so this
// says it in words:
//
//     🚪  Main door — Open
//         Door sensor · RF-000042 · hw 3344130 #9 · 3 s ago
//
// and for a sensor nobody has set up, what it probably is, with Add.
//
// ⚠️ DISPLAY ONLY. The server runs any RF actions; nothing here publishes.

import React, { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useApp } from '../../core/state/AppContext';
import useRfDirectory from '../../core/hooks/useRfDirectory';
import { parseRfV } from '../../core/devices/rfCodes';
import { meaningText } from '../../core/devices/rfIdentify';
import { isCloseEvent } from '../../core/devices/rfTypes';
import RfScanButton from './RfScanButton';

const GREEN = '#2E7D32';
const AMBER = '#ED6C02';
const GREY = '#9E9E9E';

const ago = (ms) => {
  if (!ms) return '';
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return `${s} s ago`;
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  return `${Math.floor(s / 3600)} h ago`;
};

/**
 * @param receiverUno  the receiver
 * @param status       its status string (`RfV(...)`, or 'Online' before any press)
 * @param liveAt       ms of the last live message, if any
 * @param locationId   for Add
 * @param textColor    the shell's ink (mobile passes its theme text colour)
 */
export default function RfLastHeard({
  receiverUno, status, liveAt, locationId = 0, textColor, compact = false,
}) {
  const { isSuperAdmin } = useApp();
  // A Super Admin sees the whole estate (and the signature guesses); everyone
  // else, what this receiver has heard.
  const { describe, refreshIfStale, ready } = useRfDirectory(
    isSuperAdmin ? { all: true } : { receiverUno },
  );
  const hit = parseRfV(String(status || ''));
  const d = hit && ready ? describe(hit.code) : null;

  useEffect(() => {
    if (d && d.status === 'new') refreshIfStale();
  }, [d && d.code, d && d.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const ink = textColor || 'text.primary';
  const muted = textColor ? { color: textColor, opacity: 0.7 } : { color: 'text.secondary' };

  if (!hit) {
    if (compact) return <Typography variant="caption" sx={muted}>📡 Listening — nothing heard yet</Typography>;
    return (
      <Box sx={{ textAlign: 'center', py: 1 }}>
        <Typography sx={{ fontSize: 34, lineHeight: 1 }}>📡</Typography>
        <Typography variant="body2" sx={{ ...muted, mt: 0.5 }}>Listening — nothing heard yet</Typography>
      </Box>
    );
  }
  if (!d) {
    return (
      <Typography variant="caption" sx={{ ...muted, fontFamily: 'monospace', textAlign: 'center' }}>
        {hit.code}
      </Typography>
    );
  }

  const named = d.status === 'named';
  // eslint-disable-next-line no-nested-ternary
  const colour = !named ? GREY : (isCloseEvent(d.meaning) ? GREEN : AMBER);
  // eslint-disable-next-line no-nested-ternary
  const title = named ? d.name
    : (d.status === 'unnamed' ? `Unnamed sensor ${d.rfNumber}` : (isSuperAdmin ? 'New sensor' : 'Sensor not set up'));
  const typeLine = named || !d.guessType ? d.typeLabel : `probably ${d.typeLabel}`;

  // COMPACT (the desktop room card): one line — "Last heard 🚪 Door Sensor 1 ·
  // CLOSE · 12 min ago". The sensor's own tile carries the rest. An unknown
  // sensor still gets Add.
  if (compact) {
    return (
      <Box sx={{ minWidth: 0, width: '100%' }}>
        <Typography variant="caption" sx={{ ...muted, display: 'block', lineHeight: 1.3 }}>Last heard</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Box component="span" sx={{ fontSize: 14, flexShrink: 0 }}>{named || d.guessType ? d.icon : '📡'}</Box>
          <Typography variant="body2" fontWeight={700} sx={{ color: ink, minWidth: 0 }} noWrap title={title}>{title}</Typography>
          <Typography variant="caption" fontWeight={800} sx={{ color: colour, textTransform: 'uppercase', flexShrink: 0 }}>
            {meaningText(d)}
          </Typography>
        </Box>
        {liveAt && <Typography variant="caption" sx={{ ...muted, display: 'block', lineHeight: 1.3 }}>{ago(liveAt)}</Typography>}
        {!named && (
          <RfScanButton
            receiverUno={receiverUno}
            locationId={locationId}
            initialType={d.rfType || d.guessType || ''}
            label="Add"
            sx={{ mt: 0.25 }}
          />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, textAlign: 'center', minWidth: 0, width: '100%',
    }}
    >
      <Box sx={{
        width: 48, height: 48, borderRadius: '50%', display: 'grid', placeItems: 'center',
        fontSize: 26, bgcolor: `${colour}33`, boxShadow: `0 0 0 4px ${colour}22`,
      }}
      >
        {named || d.guessType ? d.icon : '📡'}
      </Box>
      <Typography fontWeight={800} sx={{ color: ink, lineHeight: 1.2, mt: 0.5 }} noWrap title={title}>
        {title}
      </Typography>
      <Typography fontWeight={800} sx={{ color: colour, textTransform: 'uppercase', lineHeight: 1.2 }}>
        {meaningText(d)}
      </Typography>
      <Typography variant="caption" sx={{ ...muted, lineHeight: 1.3 }}>
        {[typeLine, d.rfNumber, d.roomName].filter(Boolean).join(' · ')}
      </Typography>
      <Typography variant="caption" sx={{ ...muted, fontFamily: 'monospace', lineHeight: 1.3 }}>
        {`hw ${d.transmitterId} #${d.eventByte}${liveAt ? ` · ${ago(liveAt)}` : ''}`}
      </Typography>
      {!named && (
        <RfScanButton
          receiverUno={receiverUno}
          locationId={locationId}
          initialType={d.rfType || d.guessType || ''}
          label="Add"
          sx={{ mt: 0.5 }}
        />
      )}
    </Box>
  );
}
